import { useEffect, useState } from "react";
import Cookies from "js-cookie";

import { Link, useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import { CircularProgress } from "@mui/material";

import MDAlert from "components/MDAlert";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import BasicLayout from "layouts/authentication/components/BasicLayout";

import bgImage from "assets/images/bg-sign-in-basic.jpg";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import {
  setDataConfig,
  setIsAuthencated,
  setUser,
  useMaterialUIController,
} from "context";
import { keyLocalStorage } from "helpers/constants";
import { getDadosSystem } from "helpers/getDadosSystem";
import { cpfApplyMask, numbersOnly } from "helpers/utils";
import { login } from "services/users";
import { siteConfig } from "types/siteConfig";

function Basic(): JSX.Element {
  const navigate = useNavigate();
  const [isloading, setIsloading] = useState<boolean>(false);
  const [controller, dispatch] = useMaterialUIController();
  const { transparentSidenav, darkMode, whiteSidenav, isAuthencated } = controller;
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [message, setMessage] = useState<string>();
  const [cpf, setCPF] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const handleSetRememberMe = () => {
    if (rememberMe) {
      Cookies.remove("userData");
      setRememberMe(false);
    } else {
      const userData = { cpf, password };
      const encryptedUserData = btoa(JSON.stringify(userData));
      Cookies.set("userData", encryptedUserData, { expires: 10 });
      setRememberMe(true);
    }
  };

  useEffect(() => {
    const encryptedUserData = Cookies.get("userData");

    if (encryptedUserData) {
      try {
        const userData = JSON.parse(atob(encryptedUserData));
        setCPF(userData.cpf || "");
        setPassword(userData.password || "");
        setRememberMe(true);
      } catch (error) {
        console.error("Erro ao decodificar os dados do usuario:", error);
      }
    }
  }, []);

  const handleAuthenticated = async () => {
    try {
      setIsloading(true);
      const user = await login(cpf, password);
      if (user.access_token) {
        getDadosSystem().then((data) => {
          setDataConfig(dispatch, data);
          localStorage.setItem(
            keyLocalStorage.DEFAULT_CONSTANTS,
            JSON.stringify(data)
          );
        });
        setIsAuthencated(dispatch, true);
        setUser(dispatch, user);
        navigate("/pages/relatorios/estatisticas");
      } else {
        setMessage(user.message || "Nao foi possivel realizar o login.");
        setIsloading(false);
      }
    } catch (error) {
      setMessage("Servidor temporariamente indisponivel. Tente novamente em alguns instantes.");
      setIsloading(false);
    }
  };

  const handlerForms = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    event.target.name === "cpf" && setCPF(event.target.value);
    event.target.name === "password" && setPassword(event.target.value);
  };

  useEffect(() => {
    isAuthencated && navigate("/pages/relatorios/estatisticas");
  }, []);

  return (
    <BasicLayout image={bgImage}>
      <Card
        sx={{
          width: "100%",
          mx: "auto",
          borderRadius: { xs: 2, sm: 3 },
          overflow: "visible",
        }}
      >
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={{ xs: 1.5, sm: 2 }}
          mt={{ xs: -4, sm: -5 }}
          p={{ xs: 1.5, sm: 2 }}
          mb={0}
          textAlign="center"
        >
          <MDBox mt={-3} display="flex" justifyContent="center" alignItems="center">
            <MDAvatar
              size="xxl"
              src={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              alt="TNV Team Sports"
              variant="rounded"
              sx={{
                width: { xs: 82, sm: 96 },
                height: { xs: 82, sm: 96 },
              }}
            />
          </MDBox>
          <MDTypography
            variant="h6"
            fontWeight="regular"
            color="white"
            mt={{ xs: -2, sm: -3 }}
            sx={{ fontSize: { xs: "0.9rem", sm: "1.25rem" } }}
          >
            Acesso restrito
          </MDTypography>
          <MDTypography
            variant="h4"
            fontWeight="medium"
            color="white"
            mt={0}
            sx={{ fontSize: { xs: "1.25rem", sm: "2rem" }, lineHeight: 1.15 }}
          >
            Painel do Organizador
          </MDTypography>
        </MDBox>

        <MDBox pt={1} pb={{ xs: 2.5, sm: 3 }} px={{ xs: 2, sm: 3 }}>
          <MDBox mt={1} mb={1} textAlign="center">
            {message && <MDAlert color="error">{message}</MDAlert>}
            <MDTypography
              variant="button"
              color="text"
              display="block"
              sx={{ fontSize: { xs: "0.76rem", sm: "0.875rem" }, lineHeight: 1.45 }}
            >
              Area exclusiva para organizadores.
              <MDTypography
                component={Link}
                to={siteConfig.url}
                variant="button"
                color="info"
                fontWeight="medium"
                textGradient
                display="block"
                mt={0.5}
                sx={{ fontSize: { xs: "0.76rem", sm: "0.875rem" } }}
              >
                Participante? Acesse o site.
              </MDTypography>
            </MDTypography>
          </MDBox>

          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                name="cpf"
                onChange={handlerForms}
                value={cpfApplyMask(numbersOnly(cpf))}
                type="numeric"
                label="CPF"
                fullWidth
                inputProps={{ maxLength: 14 }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                name="password"
                value={password}
                onChange={handlerForms}
                type="password"
                label="Senha"
                fullWidth
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Lembre-me
              </MDTypography>
            </MDBox>
            <MDBox mt={2} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={handleAuthenticated}
                disabled={cpf === "" || password === ""}
              >
                {isloading && <CircularProgress color="inherit" size="30px" />}
                <MDTypography
                  variant="h5"
                  color="white"
                  ml={1}
                  sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                >
                  Entrar
                </MDTypography>
              </MDButton>
            </MDBox>
            <MDBox mt={1} mb={1} textAlign="center">
              <MDTypography
                variant="button"
                color="text"
                sx={{ fontSize: { xs: "0.76rem", sm: "0.875rem" } }}
              >
                Nao tem uma conta?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up/cover"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                  sx={{ fontSize: { xs: "0.76rem", sm: "0.875rem" } }}
                >
                  Cadastrar
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
